import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Drawer, InputNumber, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import apiClient from '../../../shared/services/apiClient';
import { formatMoney, currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Text } = Typography;

const RANGE_TIPS = {
    ipv4: '最大值 = 每个账户弹性 IP 上限（0 为默认 10）',
    ipv6: '按块计价：展开填写各规格单价（0 不售）',
    memory: '单位 GB',
};

/** Rows with extra options (expanded): IPv6 block prices, per-VM caps. */
const CAP_ROWS = ['ipv6', 'ipv4', 'data_disk'];

const get = async (url, params) => {
    try { return (await apiClient.get(url, { params })).data; } catch (e) { return { status: 'error', msg: e.message }; }
};
const post = async (url, data) => {
    try { return (await apiClient.post(url, data)).data; } catch (e) { return { status: 'error', msg: e.message }; }
};

/** Paid add-ons that could not be applied: refund to balance or mark handled. */
const ReviewList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        const r = await get('/api/evm/admin/elastic/review');
        setRows(r.status === 'success' ? r.data || [] : []);
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);
    const act = async (id, action) => {
        const r = await post(`/api/evm/admin/elastic/review/${id}`, { action });
        if (r.status === 'success') { message.success(r.msg || '已处理'); load(); } else message.error(r.msg || '失败');
    };
    return (
        <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={rows}
            pagination={false}
            locale={{ emptyText: '无待处理' }}
            columns={[
                { title: '账单', dataIndex: 'invoice_number' },
                { title: '用户', dataIndex: 'user_id', width: 70 },
                { title: '内容', dataIndex: 'description', render: (v, r) => <Tooltip title={r.error}><span>{v}</span></Tooltip> },
                { title: '金额', dataIndex: 'amount', width: 80, render: (v, r) => formatMoney(v, r.currency || PLATFORM_CURRENCY) },
                {
                    title: '', key: 'a', width: 150, render: (_, r) => (
                        <Space size={4}>
                            <Popconfirm title="退款到余额？" onConfirm={() => act(r.id, 'refund')}>
                                <Button size="small" danger disabled={r.invoice_status !== 'paid'}>退款</Button>
                            </Popconfirm>
                            <Button size="small" onClick={() => act(r.id, 'resolve')}>已处理</Button>
                        </Space>
                    ),
                },
            ]}
        />
    );
};

/** Elastic resource config: nodeId 0 = global default, otherwise a node's overrides. */
const ElasticConfigDrawer = ({ open, nodeId = 0, nodeName, onClose }) => {
    const isGlobal = !nodeId;
    const [data, setData] = useState(null);
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const r = await get('/api/evm/admin/elastic/config', { node_id: nodeId });
        if (r.status === 'success') { setData(r.data); setItems(r.data.items); } else message.error(r.msg || '加载失败');
    }, [nodeId]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const set = (res, patch) => setItems((list) => list.map((it) => (it.resource === res ? { ...it, ...patch } : it)));

    const save = async () => {
        setSaving(true);
        const own = items.filter((it) => isGlobal || it.override);
        const r = await post('/api/evm/admin/elastic/config', {
            node_id: nodeId,
            items: own.map(({ resource, enabled, min, max, step, unit_price, period_months, options }) => ({
                resource, enabled: enabled ? 1 : 0, min, max, step, unit_price, period_months,
                ...(CAP_ROWS.includes(resource) ? { options: options || {} } : {}),
            })),
            inherit: isGlobal ? [] : items.filter((it) => !it.override).map((it) => it.resource),
        });
        setSaving(false);
        if (r.status === 'success') { message.success('已保存'); load(); } else message.error(r.msg || '保存失败');
    };

    const editable = (it) => isGlobal || it.override;
    const num = (it, key, props = {}) => (
        <InputNumber size="small" min={0} value={it[key]} disabled={!editable(it)} onChange={(v) => set(it.resource, { [key]: v ?? 0 })} style={{ width: 80 }} {...props} />
    );

    const columns = [
        {
            title: '资源', key: 'r', render: (_, it) => (
                <Space size={4}>
                    <Text strong>{it.label}</Text>
                    <Text type="secondary">{it.unit}</Text>
                    {RANGE_TIPS[it.resource] && <Tooltip title={RANGE_TIPS[it.resource]}><Text type="secondary">ⓘ</Text></Tooltip>}
                </Space>
            ),
        },
        ...(!isGlobal ? [{
            title: <Tooltip title="关闭则继承全局默认">节点覆盖</Tooltip>, key: 'o', width: 90,
            render: (_, it) => <Switch size="small" checked={it.override} onChange={(v) => set(it.resource, { override: v })} />,
        }] : []),
        { title: '开放', key: 'e', width: 70, render: (_, it) => <Switch size="small" checked={it.enabled} disabled={!editable(it)} onChange={(v) => set(it.resource, { enabled: v })} /> },
        { title: '最小', key: 'min', width: 90, render: (_, it) => num(it, 'min') },
        { title: <Tooltip title="CPU/内存/带宽/磁盘：单台实例总量上限">最大</Tooltip>, key: 'max', width: 90, render: (_, it) => num(it, 'max') },
        { title: '步长', key: 'step', width: 90, render: (_, it) => num(it, 'step', { min: 1 }) },
        { title: `单价 ${currencySymbol(PLATFORM_CURRENCY)}`, key: 'p', width: 100, render: (_, it) => num(it, 'unit_price', { step: 0.1, precision: 2, style: { width: 90 } }) },
        {
            title: '周期', key: 'pm', width: 90, render: (_, it) => (
                <Select size="small" value={it.period_months} disabled={!editable(it)} onChange={(v) => set(it.resource, { period_months: v })} style={{ width: 80 }}
                    options={[1, 3, 6, 12].map((m) => ({ value: m, label: `${m} 月` }))} />
            ),
        },
        { title: '状态', key: 's', width: 80, render: (_, it) => (it.effective ? <Tag color="green">生效</Tag> : <Tag>关闭</Tag>) },
    ];

    /** IPv6: price per block size (per period) + bought blocks per VM. */
    const ipv6Options = (it) => {
        const opts = it.options || {};
        const prices = opts.prices || {};
        const setOpt = (patch) => set('ipv6', { options: { ...opts, ...patch } });
        return (
            <Space wrap size={12}>
                {(data?.ipv6_lens || [64, 60, 56, 52, 48]).map((len) => (
                    <InputNumber key={len} size="small" min={0} step={1} precision={2} addonBefore={`/${len}`} disabled={!editable(it)}
                        value={prices[len] ?? prices[String(len)] ?? null} placeholder="0" style={{ width: 130 }}
                        onChange={(v) => setOpt({ prices: { ...prices, [len]: v ?? 0 } })} />
                ))}
                <Tooltip title="单台实例可购 IPv6 块（不含套餐内）">
                    <InputNumber size="small" min={0} max={64} addonBefore="每实例最多" addonAfter="块" disabled={!editable(it)}
                        value={opts.max_blocks ?? null} placeholder="1" style={{ width: 200 }}
                        onChange={(v) => setOpt({ max_blocks: v })} />
                </Tooltip>
            </Space>
        );
    };

    /** Per-VM caps (blank = default), enforced on purchase; admin changes only warn. */
    const capInput = (it, key, label, unit, placeholder, tip) => {
        const opts = it.options || {};
        return (
            <Tooltip key={key} title={tip}>
                <InputNumber size="small" min={0} addonBefore={label} addonAfter={unit} disabled={!editable(it)}
                    value={opts[key] ?? null} placeholder={placeholder} style={{ width: 200 }}
                    onChange={(v) => set(it.resource, { options: { ...opts, [key]: v } })} />
            </Tooltip>
        );
    };
    const capOptions = (it) => {
        if (it.resource === 'ipv6') return ipv6Options(it);
        if (it.resource === 'ipv4') {
            return <Space wrap size={12}>{capInput(it, 'max_per_vm', '每实例最多', '个', '2', '单台实例可购/绑定的附加 IPv4')}</Space>;
        }
        return (
            <Space wrap size={12}>
                {capInput(it, 'max_count', '每实例最多', '块', '4', '单台实例数据盘数量')}
                {capInput(it, 'max_total_gb', '合计最多', 'GB', String(it.max || ''), '单台实例数据盘总容量，留空 = 最大值')}
            </Space>
        );
    };

    const configTable = (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {data && !data.ready && <Alert type="warning" showIcon message="待执行 evm_elastic_2026_10.sql" />}
            {data && !data.selfservice && <Alert type="info" showIcon message="自助购买总开关已关闭（addons_selfservice_enabled）" />}
            <Table size="small" rowKey="resource" columns={columns} dataSource={items} pagination={false} scroll={{ x: 760 }}
                expandable={{ rowExpandable: (it) => CAP_ROWS.includes(it.resource), expandedRowRender: capOptions, defaultExpandedRowKeys: CAP_ROWS }} />
            <Text type="secondary" style={{ fontSize: 12 }}>按剩余天数折算；单价为 0 不开放（IPv4 为免费）；系统盘扩容需节点 agent 支持{data && !data.sysdisk_grow_supported ? '（未开启）' : ''}</Text>
        </Space>
    );

    return (
        <Drawer
            title={isGlobal ? '弹性配置 · 全局默认' : `弹性配置 · ${nodeName || nodeId}`}
            width={900}
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={<Button type="primary" loading={saving} disabled={data && !data.ready} onClick={save}>保存</Button>}
        >
            {isGlobal ? (
                <Tabs items={[
                    { key: 'cfg', label: '配置', children: configTable },
                    { key: 'review', label: '待处理', children: <ReviewList /> },
                ]} />
            ) : configTable}
        </Drawer>
    );
};

export default ElasticConfigDrawer;
